import React from 'react';
import { Modal, Button, Table, Input, DatePicker, Select, Space, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FM030, FM033 } from '../../../services/pwm211Api';

const { Option } = Select;

interface ReplyModalProps {
  open: boolean;
  record: FM030 | null;
  replies: FM033[];
  loading: boolean;
  saving: boolean;
  onClose: () => void;
  onAddRow: () => void;
  onRemoveRow: (idx: number) => void;
  onSetField: (idx: number, field: keyof FM033, value: unknown) => void;
  onSave: () => void;
}

const ReplyModal: React.FC<ReplyModalProps> = React.memo(({
  open, record, replies, loading, saving,
  onClose, onAddRow, onRemoveRow, onSetField, onSave,
}) => {
  const title = record
    ? `庫位回覆明細 — 庫位:${record.fm030_01} 產品:${record.fm030_02} 批號:${record.fm030_03}`
    : '庫位回覆明細';

  const columns = [
    {
      title: '項次',
      dataIndex: 'fm033_04',
      width: 60,
      render: (_: unknown, __: FM033, idx: number) => idx + 1,
    },
    {
      title: '庫存分類',
      dataIndex: 'fm033_11',
      width: 100,
      render: (val: string, _: FM033, idx: number) => (
        <Input
          value={val}
          maxLength={2}
          onChange={e => onSetField(idx, 'fm033_11', e.target.value)}
          placeholder="代號"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '產生原因',
      dataIndex: 'fm033_12',
      width: 100,
      render: (val: string, _: FM033, idx: number) => (
        <Input
          value={val}
          maxLength={2}
          onChange={e => onSetField(idx, 'fm033_12', e.target.value)}
          placeholder="代號"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '處理方式',
      dataIndex: 'fm033_13',
      width: 100,
      render: (val: string, _: FM033, idx: number) => (
        <Input
          value={val}
          maxLength={2}
          onChange={e => onSetField(idx, 'fm033_13', e.target.value)}
          placeholder="代號"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '預計完成日',
      dataIndex: 'fm033_14',
      width: 140,
      render: (val: string | null, _: FM033, idx: number) => (
        <DatePicker
          value={val ? dayjs(val) : null}
          onChange={d => onSetField(idx, 'fm033_14', d ? d.toISOString() : null)}
          style={{ width: '100%' }}
          format="YYYY/MM/DD"
        />
      ),
    },
    {
      title: '備註',
      dataIndex: 'fm033_15',
      render: (val: string, _: FM033, idx: number) => (
        <Input
          value={val}
          onChange={e => onSetField(idx, 'fm033_15', e.target.value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_: unknown, __: FM033, idx: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRemoveRow(idx)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={onSave}
          >
            儲存
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Spin spinning={loading}>
        {record && (
          <div className="pwm211-reply-info" style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
            庫存量: {record.fm030_16} &nbsp;|&nbsp;
            入庫日: {record.fm030_13 ? dayjs(record.fm030_13).format('YYYY/MM/DD') : '—'} &nbsp;|&nbsp;
            色號: {record.fm030_51} {record.fm030_511 ? `(${record.fm030_511})` : ''}
          </div>
        )}
        <Button
          icon={<PlusOutlined />}
          onClick={onAddRow}
          style={{ marginBottom: 8 }}
        >
          新增明細
        </Button>
        <Table<FM033>
          dataSource={replies}
          columns={columns}
          rowKey={(_, idx) => String(idx)}
          size="small"
          pagination={false}
          scroll={{ y: 300 }}
        />
      </Spin>
    </Modal>
  );
});

ReplyModal.displayName = 'ReplyModal';

export default ReplyModal;
